import * as React from "react";
import {reisy, use} from "reisy";
import Image from "../components/image.js";
import InlineLoader from "../components/inline-loader.js";
import SectionHeader from "./sectionheader.js";
import Link from '../components/link.js';
import {Back, ArrowLeft, ArrowRight} from '../components/icons.js';
import {injectIntl, defineMessages} from 'react-intl';
import Heading from '../components/heading';
import TagList from '../components/taglist';

const messages = defineMessages({
	placeholder: {
		id: "multislider.showAll",
		defaultMessage: "All items",
	},
});

export const styles = reisy`
@namespace Multislider;
SlideBreakpoints: 480-1, 640-2, 900-3, 1200-4;
showTags: false;

multislider {
	padding: 1em 2em 3em;

	$Page.Inner & $SectionHeader.Container {
		display: none;
	}
}

sliderWrapper {
	position: relative;
	max-width: 100%;
	margin: 0 auto;
	/*padding: 0 2em;*/

	$Media.small {
		max-width: 40em;
	}

	$Media.medium {
		max-width: 50em;
	}

	$Media.large {
		max-width: 60em;
	}

	$Page.Inner & {
		max-width: 100%;
	}
}

slidesWrapper {
	min-height: 10em;
	overflow: hidden;
	user-select: none;

	& $InlineLoader.Loader {
		background: none;
	}

	& $InlineLoader.Spinner {
		border-color: #d3d3d3;
		border-left-color: #969696;
	}
}

slides {
	display: flex;
	transition: transform 200ms ease-out;

	&.disable-transition {
		transition: none;
	}
}

moveEnd {
	order: 1;
}

slideContainer {
	display: flex;
	flex: 1 0 auto;
	justify-content: flex-end;
}

slide {
	flex: 1 0 auto;
	padding: 1rem;
	box-sizing: border-box;

	& $Image.backgroundImage {
		position: absolute;
		top: 0;
		left: 0;		
	}

	img {
		position: absolute;
		top: 0;
		left: 0;
		pointer-events: none;
	}

	& a {
		display: flex;
		flex-direction: column;
		height: 100%;
		color: $Colors.TextDark;
		text-decoration: none;
	}

	& $Image.ContainerPlaceholder {
		padding-top: 100%;
		height: auto;
	}
}

slideInfo {
	flex: 1 1 auto;
}

slideTitle {
	font-family: $Fonts.title;
	font-size: 1.1em;
	font-weight: normal;
	padding-top: .75em;

	$Page.Outer .kent-page & {
		font-family: $Fonts.title;
		font-size: 1.1em;
		font-weight: normal;
		padding-top: .75em;	
	}
}

slideSubtitle {
	font-family: $Fonts.subtitle;
	font-weight: normal;
	padding-top: .3em;
}

slideDescription {
	font-family: $Fonts.text;
	font-size: .85em;
	font-weight: normal;
	padding-top: .25em;
}

collectionLink {
	a {
		display: block;
	}
}

collectionLinkInner {
	position: relative;
	padding-top: 100%;
	background: #f2f2f2;
	border-radius: .4em;
}

collectionLinkContent {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	align-items: center;


	svg {
		height: 2em;
		width: 2em;
		fill: #2d2d2d;
	}
}

iconText {
	@extends slideTitle;
    position: relative;
	height: 0;
	padding: 0;
    top: .6em;
	text-align: center;
	color: #2d2d2d;
	font-family: $Fonts.aside;
	font-size: .9em;
}

prevNextButton {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	width: 1.75em;
	height: 1.75em;
	cursor: pointer;
	user-select: none;

	path {
		stroke: $Colors.TextDark;
	}
}
prevNext {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
}
prev {
	@extends prevNextButton;
	left: -1.75em;
}
next {
	@extends prevNextButton;
	right: -1.75em;
}
`;

const _CollectionLink = ({content, maxItems, intl}) =>
	<div {...use(styles.collectionLink, styles.slide)} style={{width: `${100 / maxItems}%`}}>
		<Link to={{append: [content, content]}} draggable={false} onDragStart={e => e.preventDefault()}>
			<div {...use(styles.collectionLinkInner)}>
				<div {...use(styles.collectionLinkContent)}>
					<Back />
					<div {...use(styles.iconText)}>
						{intl.formatMessage(messages.placeholder)}
					</div>
				</div>
			</div>
		</Link>
	</div>;

const CollectionLink = injectIntl(_CollectionLink);

const Slide = ({item, parent, maxItems, withTags}) => {
	const to = item.options && item.options.href || item.href || {append: [parent, item]};
	const target = item.type === 'link' ? (item.options && item.options.target || '_blank') : null;

	const tags = item.tags.length > 0 && item.tags.map(tag => `Tag-${tag.name.split(' ').join('-')}`);
	const taglist = styles.showTags && item.tags ? 
		<TagList publishable={item} filter={withTags.startsWith && withTags.startsWith('/') ? i => i.name.match(new RegExp(withTags.slice(1,-1))) : undefined} /> 
		: null;

	return <article {...use(styles.slide, item.options && item.options.className)} style={{width: `${100 / maxItems}%`}}>
		<Link to={to} draggable={false} onDragStart={e => e.preventDefault()} target={target}>
			<Image
				image={item.image}
				imageOptions={item.imageOptions}
				ratio={1}
				sizes="32em"
				availWidth={740}
				alt={item.title}
				cover
				placeholder />
			<div {...use(styles.slideInfo)}>
				<Heading lvl={3} {...use(styles.slideTitle)}>{item.title}</Heading>
				{item.subTitle && <p {...use(styles.slideSubtitle)}>{item.subTitle}</p>}
				{item.description && <div {...use(styles.slideDescription)}>{item.description}</div>}
				{taglist}
			</div>
		</Link>
	</article>
};


class Multislide extends React.Component {
	constructor(props) {
		super(props);
		this.sliderWrapper = React.createRef();
		this.slides = React.createRef();
		this._autoslideInterval = null;
		
		const breakpoints = styles.SlideBreakpoints
			.split(',')
			.map(s => s.trim().split('-'))
			.map(b => ({
				width: parseInt(b[0]), // minimum width
				options: {
					visibleItems: parseInt(b[1]),	// No. of items to show at once
				},
			}));
		
		this.options = {
			maxItems: props.maxItems || 5,	// Maximum no. of items to include from collection
			showLink: 'showLink' in props ? props.showLink : true,	// Whether to show link to open whole collection (is disabled if loop is true)
			loop: props.loop || false,	// Loop items infinitely
			autoslide: props.autoslide || false, // autoslide multislider; can also be object with key `interval`
			breakpoints,
		};

		if (this.options.loop) {	// Don't allow showLink with loop
			this.options.showLink = false;
		}

		if (this.options.maxItems > props.content.itemCount) {	// Can't have higher max items than items in collection
			this.options.maxItems = props.content.itemCount;
		}

		if (this.options.showLink) {
			this.options.maxItems = this.options.maxItems + 1;	// Make room for additional link, needed for simple space calculation
		}

		this.state = {
			items: props.content.content,
			loading: true,
		};

		this.index = 0;
		this.clones = [];
		this.x0 = null;
		this.y0 = null;
		this.tx = 0;
		this.itemW = 0;
		this.isTransitioning = false;
		this.innerW = IS_BROWSER ? window.innerWidth : 1024;
	}
	updateSliderOptions = options => {
		this.setState(Object.assign(options, {loading: false}), () => {
			this.clone();
			this.transform();
			this.index = 0;
			this.itemW = this.slides.current.children[0].children[0].offsetWidth;
		});
	}
	handleResize = e => {
		clearTimeout(this.timer);

		if (this.innerW !== e.target.innerWidth) {
			this.timer = setTimeout(this.handleBreakpoints, 100);
			this.innerW = e.target.innerWidth;
		}
	}
	handleBreakpoints = () => {
		for (const bp of this.options.breakpoints) {
			if (window.innerWidth < bp.width) {
				const options = bp.options;
				this.updateSliderOptions(options);
				return;
			}
		}
		this.updateSliderOptions(this.options.breakpoints[this.options.breakpoints.length - 1].options); // fallback
	}
	clone = () => {
		// Sets up cloned versions of slides to display before and after initial row
		const {content} = this.props;
		const {maxItems, showLink, loop} = this.options;
		const {items, visibleItems} = this.state;
		const itemsTruncated = items.slice(0, (showLink ? maxItems - 1 : maxItems)); // Limit items data to maxItems
		this.clones = [];

		if (loop) {
			// Clone slides 4 times for infinite loop effect
			for (let i = 0; i < 4; i++) {
				this.clones.push([...itemsTruncated]);
			}
		} else {
			const clone1 = [];
			for (let i = visibleItems; i < itemsTruncated.length; i++) {
				clone1.push(itemsTruncated[i]);
			}
			this.clones.push(clone1);
			this.clones.push([...itemsTruncated]);
		}

		if (showLink) {	// Add additional collection link to item list
			const collectionLink = {
				type: 'collection-link',
				content,
			};

			this.clones[0].unshift(collectionLink);
			this.clones[1].push(collectionLink);
		}

		this.setState({}) // force update of clones
	}
	lock = e => {
		this.x0 = this.unify(e).clientX;
		this.y0 = this.unify(e).clientY;
		this.sliderWrapper.current.addEventListener('mouseleave', this.move);
		this.stopAutoslide();
	}
	move = e => {
		const d = (a, b) => Math.abs(a - b); // difference between a, b

		const x = d(this.x0, this.unify(e).clientX); // diff x
		const y = d(this.y0, this.unify(e).clientY); // diff y

		const h = x > (y + 30); // is horizontal swipe
		this.slides.current.style.removeProperty('transition');
		this.transform();

		if (h) {
			this.sliderWrapper.current.addEventListener('click', this.handleClick);
			const dx = this.unify(e).clientX - this.x0, s = Math.sign(dx);

			if (s > 0 && !this.reachedSlideEnd("prev", this.index)) {
				this.slide('prev');
			} else if (s < 0 && !this.reachedSlideEnd("next", this.index)) {
				this.slide('next');
			}
		}

		this.x0 = null;
		this.y0 = null;
		this.sliderWrapper.current.removeEventListener('mouseleave', this.move);
	}
	drag = e => {
		if (this.x0 || this.x0 === 0) {
			const d = this.unify(e).clientX - this.x0; // diff
			const o = Math.min(Math.max(d, -this.itemW), this.itemW); // limit possible drag to the width of an item

			// prevent vertical scrolling when swiping horizontally on touch devices
			if (Math.abs(d) > 5) {
				this.slides.current.style.setProperty('transition', 'none');
				this.slides.current.style.setProperty('transform', `translateX(calc(${this.getTranslateX()}% + ${o}px))`);

				if (e.type === "touchmove") {
					e.preventDefault();
					e.returnValue = false;
					return false;
				}
			}
		}
	}
	handleClick = e => {
		e.preventDefault();
		this.sliderWrapper.current.removeEventListener('click', this.handleClick);
	}
	unify = e => e.changedTouches ? e.changedTouches[0] : e
	componentWillMount() {
		this.clone();
	}
	componentDidMount() {
		window.addEventListener('resize', this.handleResize);
		this.sliderWrapper.current.addEventListener('mousedown', this.lock);
		this.sliderWrapper.current.addEventListener('touchstart', this.lock);
		this.sliderWrapper.current.addEventListener('mouseup', this.move);
		this.sliderWrapper.current.addEventListener('touchend', this.move);
		this.sliderWrapper.current.addEventListener('mousemove', this.drag);
		this.sliderWrapper.current.addEventListener('touchmove', this.drag, {passive: false});

		this.handleBreakpoints();
		this.maybeStartAutoslide();
	}
	componentWillUnmount() {
		window.removeEventListener('resize', this.handleResize);
		this.sliderWrapper.current.removeEventListener('mousedown', this.lock);
		this.sliderWrapper.current.removeEventListener('touchstart', this.lock);
		this.sliderWrapper.current.removeEventListener('mouseup', this.move);
		this.sliderWrapper.current.removeEventListener('touchend', this.move);
		this.sliderWrapper.current.removeEventListener('mousemove', this.drag);
		this.sliderWrapper.current.removeEventListener('touchmove', this.drag, {passive: false});
		this.stopAutoslide();
	}
	maybeStartAutoslide() {
		if (this.options.autoslide) {
			this._autoslideInterval = setInterval(() => {
				this.slide(1);
			}, this.options.autoslide.interval || 3000);
		}
	}
	stopAutoslide() {
		clearInterval(this._autoslideInterval);
	}
	resetIndex = i => {
		this.index = this.index + i;
		this.transform();

		const setIndex = () => {
			this.slides.current.removeEventListener('transitionend', setIndex);
			// Disable animations and quickly swap out slides to initial position
			this.index = i;

			this.slides.current.classList.add('disable-transition');
			this.transform();

			setTimeout(() => {
				this.isTransitioning = false;
				this.slides.current.classList.remove('disable-transition');
			}, 1);
		};

		this.slides.current.addEventListener('transitionend', setIndex);
	}
	endTransition = () => {
		this.isTransitioning = false;
		this.slides.current.removeEventListener('transitionend', this.endTransition);
	}
	transform = () => {
		this.slides.current.style.setProperty('transform', `translateX(${this.getTranslateX()}%)`);
	}
	slide = direction => {
		const sign = direction === 'prev' ? -1 : 1;

		if (this.isTransitioning || this.reachedSlideEnd(direction, this.index)) {
			return;
		}

		this.isTransitioning = true;

		if (this.index >= this.options.maxItems || this.index <= this.options.maxItems * -1) {
			this.resetIndex(sign);
			return;
		}

		this.index = this.index + sign;
		this.transform();

		this.slides.current.addEventListener('transitionend', this.endTransition);
	}
	reachedSlideEnd = (direction, index) => !this.options.loop && index === ((this.options.maxItems - this.state.visibleItems) * (direction === "prev" ? -1 : 1));
	getTranslateX = () => -(this.clones[0].length / this.state.visibleItems * ((this.clones.length / 2) * 100)) + (this.index * (100 / this.state.visibleItems) * -1);
	render() {
		const {content} = this.props;
		const {maxItems} = this.options;
		const {loading, visibleItems} = this.state;
		const clones = this.clones;

		return (
			<div {...use(styles.multislider)}>
				<SectionHeader headingLvl={2} content={content} />
				 <div {...use(styles.sliderWrapper)} ref={this.sliderWrapper}>
					<div {...use(styles.prevNext)} style={loading ? {top: '50%'} : {paddingTop: `${100 / visibleItems}%`}}>
						<div {...use(styles.prev)} onClick={() => this.slide('prev')}><ArrowLeft /></div>
						<div {...use(styles.next)} onClick={() => this.slide('next')}><ArrowRight /></div>
					</div>
					<div {...use(styles.slidesWrapper)}>
						{loading
							? <InlineLoader />
							: <div {...use(styles.slides)} ref={this.slides}>
								{clones.map((clone, i) =>
									<div
										key={`container${i}`}
										{...use(styles.slideContainer)}
										style={{width: `${clone.length / visibleItems * 100}%`}}>
										{clone.map((item, idx) =>
											item.type === "collection-link"
												? <CollectionLink key={`container${i}item${idx}`} content={content} maxItems={maxItems} />
												: <Slide key={`container${i}item${idx}`} item={item} parent={content} maxItems={maxItems} withTags={styles.showTags} />
										)}
									</div>
								)}
							</div>
						}
					</div>
				</div>
			</div>
		);
	}
}

export default Multislide;



// WEBPACK FOOTER //
// ./lib/publishables/multislider.js